import {combineLatest as observableCombineLatest, of as observableOf, Observable} from 'rxjs';

import {
  map,
  mergeMap,
  startWith,
  takeWhile,
  first,
  catchError,
  switchMap,
  filter,
  shareReplay,
} from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {DragulaService} from 'ng2-dragula';
import {DisplayAttribute} from '../../resource/displayattribute';
import {CacheService} from '../../cache/cache.service';
import {ObjectTypeDescription} from '../../cache/schema/objecttypedescription';
import {SearchScopeConfiguration} from '../../cache/uiconfiguration/searchscopeconfiguration';
import {ListviewActionButton, SmdDataTable} from '../component/smd-datatable';
import {CtrlDatetimeComponent} from '../../resource/detail/controls/ctrl-datetime.component';
import {AddViewEvent} from '../../resource/events/addviewevent';
import {LanguageService} from '../../language/language.service';
import {MyParser} from '../xpath-parser/parser';
import {MyExpression} from '../xpath-parser/xpathVisitor';
import {APPCONFIG} from '../../config';
import {EnvironmentConfigService} from '../../core/services/environment-config.service';
import {ActionBarConfiguration} from '../../cache/uiconfiguration/actionbarconfiguration';
import {Title} from '@angular/platform-browser';

@Component({
  selector: 'filter-builder',
  templateUrl: './filterbuilder.component.html',
  styleUrls: ['./filterbuilder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FilterbuilderComponent implements OnInit, OnDestroy, DoCheck {
  @Input() set searchScopeConfiguration(searchScope: SearchScopeConfiguration) {
    if (searchScope == null) return;
    this.searchScope = searchScope;
    try {
      if (Array.isArray(searchScope.attributedisplayed)) {
        this.displayAttributes = searchScope.attributedisplayed;
      } else {
        this.displayAttributes = searchScope.attributedisplayed.split(';');
      }
    } catch (e) {
      this.searchScope.attributedisplayed = [
        this.envConfigService.getAttributeName('DisplayName') || 'DisplayName',
      ];
      this.displayAttributes = [
        this.envConfigService.getAttributeName('DisplayName') || 'DisplayName',
      ];
    }

    this.parseXpath(this.searchScope.xpath);
  }

  @Input() permittedObjects: string[] = [];
  @Input() permittedAttributes: string[] = [];
  @Input() showAllEligibleResourcesFilterOption: boolean = false;

  @Output() searchScopeConfigurationChange: EventEmitter<SearchScopeConfiguration> =
    new EventEmitter<SearchScopeConfiguration>();

  @ViewChild('container', {static: true}) container: ElementRef;
  @ViewChild('filterBuilderPanel', {static: true}) filterBuilderPanel: ElementRef;
  @ViewChild('otInput', {static: true}) otInput: ElementRef;
  @ViewChild('attributeFilterInput', {static: false}) attributeFilterInput: ElementRef;
  private dragOverTimeout: any = null;

  // Keyboard navigation state
  selectedFilterId: number = null;

  // Display attribute toggle feedback
  toggleFeedbackMessage: string = null;
  private feedbackTimer: any = null;

  private static availableKeywords = {
    SingleValue: {
      Binary: false,
      Boolean: ['='],
      DateTime: ['>', '<'],
      Integer: ['=', '<', '>', '>=', '<='],
      Reference: ['=', 'in', 'LoggedInUser', 'TargetObject'],
      String: ['=', 'ends-with', 'starts-with'],
      Text: ['=', 'ends-with', 'starts-with'],
      ObjectType: ['='],
    },
    MultiValue: {
      Binary: false,
      Boolean: false,
      DateTime: false,
      Integer: ['='],
      Reference: ['=', 'LoggedInUser', 'TargetObject'],
      String: ['=', 'ends-with', 'starts-with'],
      Text: ['=', 'ends-with', 'starts-with'],
      ObjectType: false,
    },
  };
  listActionBarConfigs: ActionBarConfiguration[] = null;
  filterResultTitle: Observable<string>;
  languageResource: Observable<string>;

  constructor(
    private dragulaService: DragulaService,
    private cacheService: CacheService,
    private changeDetector: ChangeDetectorRef,
    private titleService: Title,
    private envConfigService: EnvironmentConfigService,
  ) {
    // Initialize displayAttributes with dynamic value
    this.displayAttributes = [
      this.envConfigService.getAttributeName('DisplayName') || 'DisplayName',
    ];

    // Set the static reference for Filter class to use
    FilterbuilderComponent.setEnvironmentConfigService(this.envConfigService);

    try {
      this.dragulaService.destroy('filter-bag');
    } catch (e) {}
    const listAbcObs: Observable<ActionBarConfiguration[]> =
      this.cacheService.getActionBarConfigurationsForObjectType('defaultCSVExport');

    listAbcObs
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        first(),
      )
      .subscribe((abcs: ActionBarConfiguration[]) => {
        this.listActionBarConfigs = abcs;
      });
    this.languageResource = this.cacheService.getLanguageResource();
    this.filterResultTitle = this.languageResource.pipe(
      map(lr => LanguageService.translate('FilterbuilderResultTitle', 'Filter Result', lr)),
    );
  }

  isDragging: boolean = false;
  dragulaName: string = 'filter-bag';

  private _alive: boolean = true;

  ngOnDestroy(): void {
    this._alive = false;
    this.dragulaService.destroy(this.dragulaName);
    if (this.feedbackTimer) {
      clearTimeout(this.feedbackTimer);
    }
  }

  objectTypes: ObjectTypeDescription[] = [];
  attributes: Observable<any[]> = observableOf([]);
  allAttributes: Observable<any[]> = observableOf([]);
  frequentAttributes: Observable<DisplayAttribute[]> = observableOf([]);

  private nextFilterId: number = 0;

  mainFilterGroup: FilterGroup = new FilterGroup(this.nextFilterId++);

  objectTypeFormControl = new UntypedFormControl();
  attributeFilterFormControl = new UntypedFormControl();

  filteredObjectTypes: Observable<ObjectTypeDescription[]>;

  searchScope: SearchScopeConfiguration = <SearchScopeConfiguration>{};
  displayAttributesSelection: boolean = false;
  displayAttributes: string[] = [];

  ngOnInit() {
    this.languageResource.pipe(first()).subscribe(lr => {
      const title = LanguageService.translate('FilterBuilderTitle', 'Filterbuilder', lr);
      this.titleService.setTitle(title + ' | ' + APPCONFIG.pageTitle);
    });

    observableCombineLatest(
      this.cacheService.getResources().pipe(first()),
      this.cacheService.getLanguageResource().pipe(first()),
      (res, lr) => {
        const [resources, defaultOTD] = this.addDefaultResourceFilterOptions(res, lr);
        this.objectTypes = resources;

        if (
          this.selectedObjectType === null &&
          (this.searchScope === null ||
            this.searchScope.xpath === null ||
            this.searchScope.xpath === undefined ||
            this.searchScope.xpath.length === 0)
        ) {
          const personOTD = this.objectTypes.find(
            odt => odt.systemname === this.envConfigService.getObjectTypeName('Person'),
          );
          const selectedODT = personOTD ?? defaultOTD;

          this.selectedObjectType = selectedODT;
          this.objectTypeFormControl.setValue(selectedODT, {emitEvent: true});
        }
      },
    )
      .pipe(takeWhile(() => this._alive))
      .subscribe();
    this.filteredObjectTypes = this.objectTypeFormControl.valueChanges
      .pipe(takeWhile(() => this._alive))
      .pipe(
        startWith<string | ObjectTypeDescription>(''),
        map(value =>
          value instanceof ObjectTypeDescription
            ? value.displayname
            : typeof value === 'string'
              ? value
              : null,
        ),
        map((name: string) => this.filterObjectTypes(name)),
      );

    this.objectTypeFormControl.valueChanges.pipe(takeWhile(() => this._alive)).subscribe(value => {
      if (typeof value === 'object' && value != null && 'systemname' in value) {
        if (
          this.selectedObjectType != null &&
          value.systemname !== this.selectedObjectType.systemname
        ) {
          this.mainFilterGroup.filters = [];
          this.displayAttributes = [
            this.envConfigService.getAttributeName('DisplayName') || 'DisplayName',
          ];
          this.changeDetector.markForCheck();
        }
        this.selectedObjectType = value;
        this.autocompleteInvalidValue = false;
      } else {
        this.autocompleteInvalidValue = true;
      }
    });

    const allPermittedAttributes: Observable<DisplayAttribute[]> =
      this.objectTypeFormControl.valueChanges.pipe(
        startWith(null),
        mergeMap(value => {
          if (value == null) value = this.selectedObjectType;
          if (typeof value === 'object' && value != null && 'systemname' in value) {
            const attrs = this.cacheService.getDisplayAttributesForResource(value.systemname);
            if (value.id === 'default' || value.id === 'all-eligible') return attrs;
            return attrs.pipe(
              map(a =>
                a.filter(
                  at =>
                    at.Name !== 'ObjectType' &&
                    (this.permittedAttributes.length == 0 ||
                      this.permittedAttributes.includes(at.Name)),
                ),
              ),
            );
          } else {
            return observableOf([]);
          }
        }),
      );

    const filteredAttributes: Observable<DisplayAttribute[]> = observableCombineLatest(
      allPermittedAttributes,
      this.attributeFilterFormControl.valueChanges.pipe(startWith(null)),
      (attributes: DisplayAttribute[], filter: string) => {
        if (filter == null) filter = this.attributeFilterFormControl.value;
        if (filter == null || filter.trim().length === 0) return attributes;
        filter = filter.trim().toLocaleLowerCase();
        return attributes.filter(
          a =>
            a.DisplayName.toLocaleLowerCase().indexOf(filter) !== -1 ||
            a.Name.toLocaleLowerCase().indexOf(filter) !== -1,
        );
      },
    );

    const groupAttributes = (das: DisplayAttribute[]) => {
      if (das.length === 0) return [];
      das = das.sort((a, b) => a.DisplayName.localeCompare(b.DisplayName));
      const o = Object.create(null);
      das.forEach(da => {
        if (da.Name === 'ObjectType') {
          da.DataType = 'ObjectType';
        }

        let x = da.DisplayName.charAt(0).toLocaleUpperCase();
        if (x.charCodeAt(0) < 'A'.charCodeAt(0) || x.charCodeAt(0) > 'Z'.charCodeAt(0)) {
          x = '#';
        }
        if (!(x in o)) {
          o[x] = [];
        }

        o[x].push(da);
      });

      const a = [];
      Object.keys(o)
        .sort((a, b) => a.localeCompare(b))
        .forEach(key => {
          a.push({code: key, attrs: o[key]});
        });
      return a;
    };

    this.allAttributes = filteredAttributes.pipe(map(das => groupAttributes(das)));

    // Create a combined stream of frequent attribute names for filtering
    const frequentAttributeNames$ = this.objectTypeFormControl.valueChanges.pipe(
      startWith(this.selectedObjectType),
      filter(value => value && typeof value === 'object' && value.systemname),
      switchMap(objectType => {
        const systemName =
          objectType.systemname === 'Resource'
            ? objectType.id === 'all-eligible'
              ? 'Resource'
              : objectType.systemname
            : objectType.systemname;
        return this.cacheService.getFrequentAttributesForObjectType(systemName);
      }),
      startWith([]),
    );

    // Filter out frequent attributes from the regular alphabetical list
    this.attributes = filteredAttributes.pipe(map(das => groupAttributes(das)));

    // Populate frequent attributes based on search scope usage
    this.frequentAttributes = observableCombineLatest(
      frequentAttributeNames$,
      allPermittedAttributes,
      (frequentNames, displayAttrs) => {
        if (frequentNames.length === 0) {
          return [];
        }
        // Map attribute names to DisplayAttribute objects
        const frequentDisplayAttrs = [];
        frequentNames.forEach(attrName => {
          const displayAttr = displayAttrs.find(da => da.Name === attrName);
          if (displayAttr && FilterbuilderComponent.keywordsFor(displayAttr).length > 0) {
            frequentDisplayAttrs.push(displayAttr);
          }
        });
        return frequentDisplayAttrs;
      },
    ).pipe(shareReplay(1));

    this.changeDetector.markForCheck();

    this.dragulaService.createGroup(this.dragulaName, {
      accepts: (el, targetEl, source, sibling) => {
        const target = targetEl as HTMLElement;
        if (
          el != null &&
          el.lastElementChild != null &&
          el.lastElementChild.classList.contains('filterGroup')
        ) {
          return (
            target != null &&
            target.dataset != null &&
            target.dataset.filterid != null &&
            target.dataset.filterid == this.mainFilterGroup.id.toString()
          );
        } else if (
          el != null &&
          el.lastElementChild != null &&
          el.lastElementChild instanceof HTMLElement &&
          el.lastElementChild.dataset.filterid != null &&
          target != null
        ) {
          return (
            target.parentElement.dataset.filterid == null ||
            target.parentElement.dataset.filterid != el.lastElementChild.dataset.filterid
          );
        } else {
          return true;
        }
      },
    });

    this.dragulaService
      .drag(this.dragulaName)
      .pipe(takeWhile(() => this._alive))
      .subscribe(({name, el, source}) => {
        if (el.lastElementChild == null || !el.lastElementChild.classList.contains('filterGroup')) {
          this.isDragging = true;
          this.changeDetector.markForCheck();
        }
      });

    this.dragulaService
      .dragend(this.dragulaName)
      .pipe(takeWhile(() => this._alive))
      .subscribe(event => {
        this.isDragging = false;
        this.changeDetector.markForCheck();
      });

    this.dragulaService
      .over(this.dragulaName)
      .pipe(takeWhile(() => this._alive))
      .subscribe(({name, el, container, source}) => {
        if (container != null && container.classList.contains('group-drop-zone')) {
          const overElements =
            this.filterBuilderPanel.nativeElement.querySelectorAll('.group-drop-zone.over');
          overElements.forEach(elem => elem.classList.remove('over'));
          if (this.dragOverTimeout != null) {
            //clearTimeout(this.dragOverTimeout);
          }
          container.classList.add('over');
        }
      });

    this.dragulaService
      .out(this.dragulaName)
      .pipe(takeWhile(() => this._alive))
      .subscribe(({name, el, container, source}) => {
        if (container != null && container.classList.contains('group-drop-zone')) {
          this.dragOverTimeout = setTimeout(() => {
            container.classList.remove('over');
          }, 200);
        }
      });

    this.dragulaService
      .drop(this.dragulaName)
      .pipe(takeWhile(() => this._alive))
      .subscribe(({name, el, target, source, sibling}) => {
        if (el == null || target == null || !(el instanceof HTMLElement)) return;
        let elementFilterId: number = null;
        if (el.dataset != null && el.dataset.filterid != null) {
          elementFilterId = parseInt(el.dataset.filterid);
        } else if (
          el.lastElementChild != null &&
          el.lastElementChild instanceof HTMLElement &&
          el.lastElementChild.dataset != null &&
          el.lastElementChild.dataset.filterid != null
        ) {
          elementFilterId = parseInt(el.lastElementChild.dataset.filterid);
        } else {
          return;
        }
        if (elementFilterId == null || isNaN(elementFilterId)) return;

        let sourceFilterId: number = null;
        if (
          source != null &&
          source instanceof HTMLElement &&
          source.dataset != null &&
          source.dataset.filterid != null
        ) {
          sourceFilterId = parseInt(source.dataset.filterid);
        } else if (
          source != null &&
          source instanceof HTMLElement &&
          source.parentElement != null &&
          source.parentElement.dataset != null &&
          source.parentElement.dataset.filterid != null
        ) {
          sourceFilterId = parseInt(source.parentElement.dataset.filterid);
        } else {
          return;
        }
        if (sourceFilterId == null || isNaN(sourceFilterId)) return;

        if (target.classList.contains('group-drop-zone')) {
          // Create Subgroup
          let el2FilterId: number = parseInt(target.parentElement.dataset.filterid);
          let el2Filter: Filter = null;
          let targetArrayIndex: number = null;
          let sourceFilterGroup: FilterGroup = null;
          for (let i = 0; i < this.mainFilterGroup.filters.length; i++) {
            const x = this.mainFilterGroup.filters[i];
            if (x.id === el2FilterId && x instanceof Filter) {
              el2Filter = <Filter>this.mainFilterGroup.filters.splice(i, 1)[0];
              targetArrayIndex = i;
              i--;
            } else if (x.id === sourceFilterId && x instanceof FilterGroup) {
              sourceFilterGroup = x;
            }
          }
          if (sourceFilterId === this.mainFilterGroup.id) {
            sourceFilterGroup = this.mainFilterGroup;
          }

          if (sourceFilterGroup == null) {
            console.error('source is null', sourceFilterId, this.mainFilterGroup);
            return;
          }

          for (let i = 0; i < sourceFilterGroup.filters.length; i++) {
            const x = sourceFilterGroup.filters[i];
            if (x.id === elementFilterId && x instanceof Filter) {
              const fg = new FilterGroup(this.nextFilterId++);
              fg.filters.push(el2Filter);
              fg.filters.push(x);
              this.mainFilterGroup.filters.splice(targetArrayIndex, 0, fg);
              if (this.mainFilterGroup === sourceFilterGroup && targetArrayIndex <= i) {
                sourceFilterGroup.filters.splice(i + 1, 1);
              } else {
                sourceFilterGroup.filters.splice(i, 1);
              }
              break;
            }
          }

          if (
            sourceFilterGroup !== this.mainFilterGroup &&
            sourceFilterGroup.filters.length === 1
          ) {
            const index = this.mainFilterGroup.filters.map(f => f.id).indexOf(sourceFilterGroup.id);
            this.mainFilterGroup.filters.splice(index, 1, sourceFilterGroup.filters[0]);
          }

          this.changeDetector.markForCheck();
          return;
        }

        let targetFilterId: number = null;
        if (
          target instanceof HTMLElement &&
          target.dataset != null &&
          target.dataset.filterid != null
        ) {
          targetFilterId = parseInt(target.dataset.filterid);
        } else if (
          target.parentElement != null &&
          target.parentElement.dataset != null &&
          target.parentElement.dataset.filterid != null
        ) {
          targetFilterId = parseInt(target.parentElement.dataset.filterid);
        } else {
          return;
        }
        if (targetFilterId == null || isNaN(targetFilterId)) return;

        let siblingFilterId: number = null;
        if (
          sibling != null &&
          sibling instanceof HTMLElement &&
          sibling.dataset != null &&
          sibling.dataset.filterid != null
        ) {
          siblingFilterId = parseInt(sibling.dataset.filterid);
        } else if (
          sibling != null &&
          sibling.lastElementChild != null &&
          sibling.lastElementChild instanceof HTMLElement &&
          sibling.lastElementChild.dataset != null &&
          sibling.lastElementChild.dataset.filterid != null
        ) {
          siblingFilterId = parseInt(sibling.lastElementChild.dataset.filterid);
        }

        if (targetFilterId !== sourceFilterId) {
          if (sourceFilterId === this.mainFilterGroup.id) {
            let targetFilterGroup: FilterGroup = null;
            let elementFilter: Filter = null;
            let elementFilterArrayIndex: number = null;
            for (let i = 0; i < this.mainFilterGroup.filters.length; i++) {
              const x = this.mainFilterGroup.filters[i];
              if (x.id === elementFilterId && x instanceof Filter) {
                elementFilter = x;
                elementFilterArrayIndex = i;
              }
              if (x.id === targetFilterId && x instanceof FilterGroup) targetFilterGroup = x;
            }

            if (
              targetFilterGroup != null &&
              elementFilter != null &&
              elementFilterArrayIndex != null
            ) {
              if (siblingFilterId != null) {
                for (let i = 0; i < targetFilterGroup.filters.length; i++) {
                  if (targetFilterGroup.filters[i].id === siblingFilterId) {
                    targetFilterGroup.filters.splice(i, 0, elementFilter);
                    break;
                  }
                }
              } else {
                targetFilterGroup.filters.push(elementFilter);
              }
              this.mainFilterGroup.filters.splice(elementFilterArrayIndex, 1);
            }
          } else {
            // source is subfiltergroup
            let targetFilterGroup: FilterGroup = null;
            let elementFilter: Filter = null;
            let elementFilterArrayIndex: number = null;
            let sourceFilterGroup: FilterGroup = null;
            let sourceFilterGroupArrayIndex: number = null;

            for (let i = 0; i < this.mainFilterGroup.filters.length; i++) {
              const x = this.mainFilterGroup.filters[i];
              if (x instanceof FilterGroup) {
                if (x.id === sourceFilterId) {
                  sourceFilterGroup = x;
                  sourceFilterGroupArrayIndex = i;
                } else if (x.id === targetFilterId) {
                  targetFilterGroup = x;
                }
              }
            }

            if (targetFilterId === this.mainFilterGroup.id) {
              targetFilterGroup = this.mainFilterGroup;
            }

            for (let i = 0; i < sourceFilterGroup.filters.length; i++) {
              const x = sourceFilterGroup.filters[i];
              if (x.id === elementFilterId && x instanceof Filter) {
                elementFilter = x;
                elementFilterArrayIndex = i;
                break;
              }
            }

            if (
              targetFilterGroup != null &&
              elementFilter != null &&
              elementFilterArrayIndex != null &&
              sourceFilterGroup != null &&
              sourceFilterGroupArrayIndex != null
            ) {
              sourceFilterGroup.filters.splice(elementFilterArrayIndex, 1);

              if (siblingFilterId != null) {
                for (let i = 0; i < targetFilterGroup.filters.length; i++) {
                  if (targetFilterGroup.filters[i].id === siblingFilterId) {
                    targetFilterGroup.filters.splice(i, 0, elementFilter);
                    if (
                      targetFilterGroup === this.mainFilterGroup &&
                      sourceFilterGroupArrayIndex >= i
                    ) {
                      sourceFilterGroupArrayIndex++;
                    }
                    break;
                  }
                }
              } else {
                targetFilterGroup.filters.push(elementFilter);
              }

              if (sourceFilterGroup.filters.length === 1) {
                this.mainFilterGroup.filters.splice(
                  sourceFilterGroupArrayIndex,
                  1,
                  sourceFilterGroup.filters[0],
                );
              } else if (sourceFilterGroup.filters.length === 0) {
                this.mainFilterGroup.filters.splice(sourceFilterGroupArrayIndex, 1);
              }
            }
          }
        } else {
          if (sourceFilterId === this.mainFilterGroup.id) {
            let element: Filter | FilterGroup = null;
            for (let i = 0; i < this.mainFilterGroup.filters.length; i++) {
              if (this.mainFilterGroup.filters[i].id === elementFilterId) {
                element = this.mainFilterGroup.filters.splice(i, 1)[0];
                break;
              }
            }
            if (siblingFilterId == null) {
              this.mainFilterGroup.filters.push(element);
            } else {
              for (let i = 0; i < this.mainFilterGroup.filters.length; i++) {
                if (this.mainFilterGroup.filters[i].id === siblingFilterId) {
                  this.mainFilterGroup.filters.splice(i, 0, element);
                  break;
                }
              }
            }
          } else {
            let sourceFilterGroup: FilterGroup = null;
            for (let i = 0; i < this.mainFilterGroup.filters.length; i++) {
              const x = this.mainFilterGroup.filters[i];
              if (x.id === sourceFilterId && x instanceof FilterGroup) {
                sourceFilterGroup = x;
                break;
              }
            }

            if (sourceFilterGroup != null) {
              let element: Filter | FilterGroup = null;
              for (let i = 0; i < sourceFilterGroup.filters.length; i++) {
                if (sourceFilterGroup.filters[i].id === elementFilterId) {
                  element = sourceFilterGroup.filters.splice(i, 1)[0];
                  break;
                }
              }
              if (siblingFilterId == null) {
                sourceFilterGroup.filters.push(element);
              } else {
                for (let i = 0; i < sourceFilterGroup.filters.length; i++) {
                  if (sourceFilterGroup.filters[i].id === siblingFilterId) {
                    sourceFilterGroup.filters.splice(i, 0, element);
                    break;
                  }
                }
              }
            }
          }
        }
        const filtersToRemove = this.filterBuilderPanel.nativeElement.querySelectorAll(
          '.mainFilterGroup > div > .filter',
        );
        filtersToRemove.forEach(filter => filter.remove());
        this.changeDetector.markForCheck();
      });

    this.onAddView.pipe(takeWhile(() => this._alive)).subscribe((e: AddViewEvent) => {
      window.open('#/' + e.siteType + '/' + e.urlid);
    });
    const tooltip = this.cacheService
      .getLanguageResource()
      .pipe(map(lr => LanguageService.translate('Refresh', 'Refresh', lr)));
    this.dataTable.header.listviewActionButtons = [
      new ListviewActionButton('refresh', tooltip, () => this.dataTable.refreshData()),
    ];
  }

  @Output() onAddView: EventEmitter<AddViewEvent> = new EventEmitter<AddViewEvent>();

  @ViewChild('searchResult', {static: true}) private dataTable: SmdDataTable;

  ngDoCheck(): void {
    if (this.selectedObjectType == null || this.mainFilterGroup == null) {
      this.searchScope = <SearchScopeConfiguration>{};
      return;
    }

    let sscXpath: string;

    if (
      this.xpathUnsupported &&
      !this.xpathUnknown &&
      !this.xpathInvalid &&
      this.xpathCustom != null
    ) {
      sscXpath = this.xpathCustom;
    } else if (this.selectedObjectType.id === 'all-eligible') {
      sscXpath = "/*[((ObjectType = 'Person') or (ObjectType = 'Group'))";
      if (this.mainFilterGroup.xpath.length !== 0 && this.mainFilterGroup.xpath !== '()') {
        sscXpath += ' and (' + this.mainFilterGroup.xpath + ')';
      }
      sscXpath += ']';
    } else {
      sscXpath = '/';
      if (this.selectedObjectType.id === 'default') {
        sscXpath += '*';
      } else {
        sscXpath += this.selectedObjectType.systemname;
      }

      const x = this.mainFilterGroup.xpath;

      if (x.length !== 0 && x !== '()') {
        sscXpath += '[' + x + ']';
      }
    }

    const joinedDA = this.displayAttributes.join(';');
    let scDA = this.searchScope.attributedisplayed;
    if (Array.isArray(scDA)) {
      scDA = scDA.join(';');
    }

    if (sscXpath !== this.searchScope.xpath || joinedDA !== scDA) {
      if (
        this.searchScope.id === undefined &&
        (this.searchScope.displayname == null || this.searchScope.displayname.length === 0)
      ) {
        this.searchScope = <SearchScopeConfiguration>{
          id: null,
          displayname: 'Filter Builder',
          description: 'Filter Builder',
          usagekeyword: [],
          order: 0,
          attributesearched: [
            this.envConfigService.getAttributeName('DisplayName') || 'DisplayName',
          ],
          xpath: sscXpath,
          resultobjecttype: this.selectedObjectType.systemname,
          attributedisplayed: joinedDA.split(';'),
        };
      } else {
        this.searchScope.xpath = sscXpath;
        this.searchScope.resultobjecttype = this.selectedObjectType.systemname;
        this.searchScope.attributedisplayed = joinedDA.split(';');
      }

      this.searchScopeConfigurationChange.emit(this.searchScope);
      this.dataTable.reloadList([this.searchScope]);
    }
  }

  private filterObjectTypes(name: string): ObjectTypeDescription[] {
    let ret: ObjectTypeDescription[];
    if (
      name == null ||
      this.permittedObjects === null ||
      this.permittedObjects === undefined ||
      name.trim().length === 0
    ) {
      ret = this.objectTypes;
    } else {
      name = name.toLocaleLowerCase();
      const startDN: ObjectTypeDescription[] = [];
      const startSN: ObjectTypeDescription[] = [];
      const containsDN: ObjectTypeDescription[] = [];
      const containsSN: ObjectTypeDescription[] = [];
      this.objectTypes.forEach((ot: ObjectTypeDescription) => {
        const indexDN = ot.displayname.toLocaleLowerCase().indexOf(name);
        if (indexDN === 0) {
          startDN.push(ot);
        } else {
          const indexSN = ot.systemname.toLocaleLowerCase().indexOf(name);
          if (indexSN === 0) {
            startSN.push(ot);
          } else if (indexDN !== -1) {
            containsDN.push(ot);
          } else if (indexSN !== -1) {
            containsSN.push(ot);
          }
        }
      });
      ret = startDN.concat(startSN).concat(containsDN).concat(containsSN);
    }
    if (this.showAllEligibleResourcesFilterOption === false) {
      ret = ret.filter((objectType: ObjectTypeDescription) => objectType.id !== 'all-eligible');
    }
    return ret.filter(
      (objectType: ObjectTypeDescription) =>
        this.permittedObjects.length == 0 ||
        this.permittedObjects.includes(objectType.systemname) ||
        (this.showAllEligibleResourcesFilterOption && objectType.id === 'all-eligible'),
    );
  }

  selectedObjectType: ObjectTypeDescription = null;
  autocompleteInvalidValue: boolean = false;

  blurObjectType() {
    if (this.autocompleteInvalidValue) {
      if (this.objectTypeFormControl.value === '') {
        this.objectTypeFormControl.setValue(this.selectedObjectType, {emitEvent: true});
      } else {
        setTimeout(() => {
          if (this.autocompleteInvalidValue) {
            this.objectTypeFormControl.setValue(this.selectedObjectType, {emitEvent: true});
          }
        }, 500);
      }
    }
  }

  private disableObjectTypeInputFocus: boolean = false;

  focusObjectType() {
    if (this.disableObjectTypeInputFocus) {
      this.otInput.nativeElement.blur();
      return;
    }

    this.autocompleteInvalidValue = true;
    this.objectTypeFormControl.setValue('', {emitEvent: false});
  }

  objectTypeSelected() {
    this.otInput.nativeElement.blur();
    this.disableObjectTypeInputFocus = true;
    setTimeout(() => {
      this.disableObjectTypeInputFocus = false;
    }, 500);
  }

  // GUI Display Helpers

  isFilterGroup(x: Filter | FilterGroup): boolean {
    return x instanceof FilterGroup;
  }

  displayFn(otd?: ObjectTypeDescription): string | undefined {
    return otd ? otd.displayname : undefined;
  }

  static keywordsFor(attribute: DisplayAttribute) {
    let kw;
    if (attribute.Multivalued) {
      kw = FilterbuilderComponent.availableKeywords.MultiValue[attribute.DataType];
    } else {
      kw = FilterbuilderComponent.availableKeywords.SingleValue[attribute.DataType];
    }
    if (kw == null || kw === false) {
      return [];
    } else {
      return kw;
    }
  }

  isDisplayedAttribute(attr: DisplayAttribute): boolean {
    if (this.displayAttributes == null) return false;
    return this.displayAttributes.indexOf(attr.Name) !== -1;
  }

  get displayAttributeCount(): number {
    return this.displayAttributes ? this.displayAttributes.length : 0;
  }

  // Event Handler
  addFilter(attribute: DisplayAttribute): void {
    if (this.xpathUnsupported) {
      this.mainFilterGroup = new FilterGroup(0);
      this.nextFilterId = 1;
    }
    this.xpathUnsupported = false;
    this.xpathInvalid = false;
    this.xpathUnknown = false;
    this.mainFilterGroup.filters.push(new Filter(this.nextFilterId++, attribute));
  }

  removeFilter(filter: Filter, group: FilterGroup) {
    const index = group.filters.indexOf(filter);
    if (index !== -1) group.filters.splice(index, 1);
    if (group !== this.mainFilterGroup && group.filters.length === 1) {
      const index = this.mainFilterGroup.filters.indexOf(group);
      if (index !== -1) this.mainFilterGroup.filters.splice(index, 1, group.filters[0]);
    }
    this.changeDetector.markForCheck();
  }

  changeDisplayedAttribute(add: boolean, attr: DisplayAttribute): void {
    if (add === true) {
      this.displayAttributes.push(attr.Name);
    } else {
      const index = this.displayAttributes.indexOf(attr.Name);
      if (index !== -1) {
        this.displayAttributes.splice(index, 1);
      }
    }
    this.ngDoCheck();
  }

  clearAttributeFilter(): void {
    this.attributeFilterFormControl.setValue('');
    this.changeDetector.markForCheck();
  }

  // Keyboard navigation methods
  @HostListener('document:keydown', ['$event'])
  handleGlobalKeydown(event: KeyboardEvent): void {
    // Ctrl+F to focus search
    if (event.ctrlKey && event.key === 'f') {
      event.preventDefault();
      this.focusAttributeFilter();
      return;
    }

    // Don't process typeahead if typing in an input
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    // Handle typeahead for attribute jumping
    if (!event.ctrlKey && !event.altKey && !event.metaKey && event.code.startsWith('Key')) {
      this.attributeFilterFormControl.setValue(event.key, {emitEvent: true});
      this.focusAttributeFilter();
    }
  }

  focusAttributeFilter(): void {
    if (this.attributeFilterInput && this.attributeFilterInput.nativeElement) {
      this.attributeFilterInput.nativeElement.focus();
      this.attributeFilterInput.nativeElement.select();
    }
  }

  handleAttributeFilterKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();

      if (this.displayAttributesSelection) {
        // Handle display attribute selection mode
        this.handleEnterForDisplayAttributes();
      } else {
        // Handle filter creation mode
        this.handleEnterForFilters();
      }
    } else if (event.key === 'Escape') {
      this.clearAttributeFilter();
      (event.target as HTMLElement).blur();
    }
  }

  private handleEnterForDisplayAttributes(): void {
    // Use allAttributes for display selection (includes ALL attributes)
    this.allAttributes.pipe(first()).subscribe(groups => {
      // Search through filtered attributes
      const filterValue = this.attributeFilterFormControl.value;
      const filter = filterValue ? filterValue.trim().toLowerCase() : null;

      for (const group of groups) {
        if (group.attrs && group.attrs.length > 0) {
          // Find first matching attribute if filter is active
          let targetAttr = group.attrs[0];
          if (filter) {
            const match = group.attrs.find(
              attr =>
                attr.DisplayName.toLowerCase().includes(filter) ||
                attr.Name.toLowerCase().includes(filter),
            );
            if (match) {
              targetAttr = match;
            } else {
              continue; // Skip this group if no match
            }
          }

          // Toggle the display attribute
          const isCurrentlyDisplayed = this.isDisplayedAttribute(targetAttr);
          this.changeDisplayedAttribute(!isCurrentlyDisplayed, targetAttr);
          this.clearAttributeFilter();

          // Provide visual feedback
          this.showAttributeToggleFeedback(targetAttr, !isCurrentlyDisplayed);
          return; // Exit after first match
        }
      }
    });
  }

  private handleEnterForFilters(): void {
    // Use attributes for filter creation (excludes certain attributes)
    this.attributes.pipe(first()).subscribe(groups => {
      for (const group of groups) {
        if (group.attrs && group.attrs.length > 0) {
          this.addFilter(group.attrs[0]);
          this.clearAttributeFilter();
          break;
        }
      }
    });
  }

  private showAttributeToggleFeedback(attr: DisplayAttribute, added: boolean): void {
    // Visual feedback for keyboard action
    this.toggleFeedbackMessage = added
      ? `Added "${attr.DisplayName}" to display columns`
      : `Removed "${attr.DisplayName}" from display columns`;

    // Clear existing timer if any
    if (this.feedbackTimer) {
      clearTimeout(this.feedbackTimer);
    }

    // Clear feedback after 2 seconds
    this.feedbackTimer = setTimeout(() => {
      this.toggleFeedbackMessage = null;
      this.changeDetector.markForCheck();
    }, 2000);

    this.changeDetector.markForCheck();
  }

  handleFilterKeydown(event: KeyboardEvent, filter: Filter, group: FilterGroup): void {
    if (event.key === 'Delete') {
      event.preventDefault();
      this.removeFilter(filter, group);
    }
  }

  selectFilter(filterId: number): void {
    this.selectedFilterId = filterId;
    this.changeDetector.markForCheck();
  }

  xpathInvalid: boolean = false;
  xpathUnsupported: boolean = false;
  xpathUnknown: boolean = false;

  xpathCustom: string;

  onParse(): void {
    this.parseXpath(this.xpathCustom);
  }

  parseXpath(xpath: string): void {
    this.xpathInvalid = false;
    this.xpathUnknown = false;
    this.xpathUnsupported = false;

    let exp: MyExpression;
    try {
      exp = MyParser.parse(xpath, this.envConfigService);
    } catch (e) {
      console.error('XPath Parsing Error', e);
      this.xpathInvalid = true;
      this.changeDetector.markForCheck();
      return;
    }

    if (
      exp.mainFilterGroup != null &&
      exp.mainFilterGroup.filters != null &&
      exp.mainFilterGroup.filters.length === 1
    ) {
      // default all operator (single filter)
      exp.mainFilterGroup.operator = 'all';
    }

    // Detect "all eligible resources" filter and remove it
    let isAllEligibleResources = false;
    if (
      exp.objectType === '*' &&
      exp.mainFilterGroup != null &&
      exp.mainFilterGroup.filters != null &&
      exp.mainFilterGroup.filters.length > 0
    ) {
      if (exp.mainFilterGroup.operator === 'all') {
        for (let i = 0; i < exp.mainFilterGroup.filters.length; i++) {
          const f = exp.mainFilterGroup.filters[i];
          if (f instanceof FilterGroup && FilterbuilderComponent.isAllEligibleFilterGroup(f)) {
            isAllEligibleResources = true;
            // remove all eligible filters
            if (
              exp.mainFilterGroup.filters.length === 2 &&
              exp.mainFilterGroup.filters[1 - i] instanceof FilterGroup
            ) {
              exp.mainFilterGroup = <FilterGroup>exp.mainFilterGroup.filters[1 - i];
            } else {
              exp.mainFilterGroup.filters.splice(i, 1);
            }
            break;
          }
        }
      } else if (FilterbuilderComponent.isAllEligibleFilterGroup(exp.mainFilterGroup)) {
        isAllEligibleResources = true;
        // remove all eligible filters
        exp.mainFilterGroup = null;
      }
    }

    if (exp.mainFilterGroup != null) {
      let cnt = 0;
      exp.mainFilterGroup.id = cnt++;
      if (Array.isArray(exp.mainFilterGroup.filters)) {
        for (let i = 0; i < exp.mainFilterGroup.filters.length; i++) {
          const f = exp.mainFilterGroup.filters[i];
          f.id = cnt++;
          if (f instanceof FilterGroup && Array.isArray(f.filters)) {
            for (let j = 0; j < f.filters.length; j++) {
              f.filters[j].id = cnt++;
              if (f.filters[j] instanceof FilterGroup) {
                this.xpathUnsupported = true;
                if (this.xpathCustom == null || this.xpathCustom.length === 0) {
                  this.xpathCustom = xpath;
                }
                this.mainFilterGroup = new FilterGroup(0);
                this.changeDetector.markForCheck();
                return;
              }
            }
          }
        }
      }
      this.nextFilterId = cnt;
    }

    observableCombineLatest(
      this.cacheService.getResources().pipe(first()),
      this.cacheService.getLanguageResource().pipe(first()),
      (res, lr) => {
        return this.addDefaultResourceFilterOptions(res, lr)[0];
      },
    )
      .pipe(first())
      .subscribe((otds: ObjectTypeDescription[]) => {
        let otd: ObjectTypeDescription;
        if (isAllEligibleResources === true) {
          otd = otds.find(x => x.id === 'all-eligible');
        } else if (exp.objectType === '*') {
          otd = otds.find(x => x.id === 'default');
        } else {
          otd = otds.find(x => x.systemname === exp.objectType);
        }
        if (otd == undefined) {
          this.xpathUnknown = true;
          console.error('ObjectType not found in objectTypes[]', exp.objectType, otds, exp);
          return;
        }

        this.selectedObjectType = otd;
        this.objectTypeFormControl.setValue(otd, {emitEvent: true});

        if (exp.mainFilterGroup == null) {
          this.mainFilterGroup = new FilterGroup(0);
          this.changeDetector.markForCheck();
          return;
        }

        const objectSystemName = exp.objectType === '*' ? 'Resource' : exp.objectType;
        const attrs = this.cacheService.getDisplayAttributesForResource(objectSystemName);

        attrs.pipe(first()).subscribe((atts: DisplayAttribute[]) => {
          const mfg = exp.mainFilterGroup;
          try {
            this.replaceLocalDisplayAttributes(mfg, atts);
          } catch (e) {
            console.error(e ? (e.message ? e.message : e) : e);
            this.xpathUnknown = true;
            this.changeDetector.markForCheck();
            return;
          }
          this.mainFilterGroup = mfg;
          this.changeDetector.markForCheck();
        });
      });
  }

  private static isAllEligibleFilterGroup(fg: FilterGroup): boolean {
    if (
      fg.operator === 'any' &&
      fg.filters != null &&
      fg.filters.length === 2 &&
      fg.filters[0] instanceof Filter &&
      fg.filters[1] instanceof Filter
    ) {
      // ((ObjectType = 'Person') or (ObjectType = 'Group'))
      const f0: Filter = <Filter>fg.filters[0];
      const f1: Filter = <Filter>fg.filters[1];
      if (
        f0.attribute != null &&
        f0.attribute.Name === 'ObjectType' &&
        f0.keyword === '=' &&
        f0.negate == false &&
        f1.attribute != null &&
        f1.attribute.Name === 'ObjectType' &&
        f1.keyword === '=' &&
        f1.negate == false &&
        ((f0.search === 'Person' && f1.search === 'Group') ||
          (f0.search === 'Group' && f1.search === 'Person'))
      ) {
        return true;
      }
    }
    return false;
  }

  private static envConfigInstance: EnvironmentConfigService;

  static setEnvironmentConfigService(envConfigService: EnvironmentConfigService) {
    FilterbuilderComponent.envConfigInstance = envConfigService;
  }

  static getAttributeName(key: string): string {
    return FilterbuilderComponent.envConfigInstance?.getAttributeName(key as any) || key;
  }

  private addDefaultResourceFilterOptions(
    res: ObjectTypeDescription[],
    lr: string,
  ): [ObjectTypeDescription[], ObjectTypeDescription] {
    const defaultOTD = <ObjectTypeDescription>{
      id: 'default',
      displayname: LanguageService.translate('FilterbuilderAllResources', 'All Resources', lr),
      description: '',
      systemname: 'Resource',
    };
    const allEligibleResourcesOTD = <ObjectTypeDescription>{
      id: 'all-eligible',
      displayname: LanguageService.translate(
        'FilterbuilderAllEligibleResources',
        'All Eligible Resources',
        lr,
      ),
      description: '',
      systemname: this.envConfigService.getObjectTypeName('Resource') || 'Resource',
    };
    const resources = res.slice();
    resources.push(defaultOTD);
    resources.push(allEligibleResourcesOTD);
    return [resources, defaultOTD];
  }

  private replaceLocalDisplayAttributes(fg: FilterGroup, atts: DisplayAttribute[]) {
    fg.filters.forEach(f => {
      if (f instanceof FilterGroup) {
        this.replaceLocalDisplayAttributes(f, atts);
      } else if (f instanceof Filter) {
        if (f.attribute.DataType === null && f.attribute.ObjectID === null) {
          let found = false;
          for (let i = 0; i < atts.length; i++) {
            if (f.attribute.Name === atts[i].Name) {
              f.attribute.DisplayName = atts[i].DisplayName;
              f.attribute.Description = atts[i].Description;
              f.attribute.Caption = atts[i].Caption;
              f.attribute.DataType = atts[i].DataType;
              f.attribute.Multivalued = atts[i].Multivalued;
              f.attribute.StringRegex = atts[i].StringRegex;
              f.attribute.AttributeID = atts[i].AttributeID;
              f.attribute.ObjectID = atts[i].ObjectID;
              found = true;
              break;
            }
          }
          if (!found) {
            throw new Error('DisplayAttribute for ' + f.attribute.Name + ' not found');
          }
        }
      }
    });
  }

  scrollToTop() {
    this.container?.nativeElement.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });

    window.scrollTo({
      // does not work in dialog
      top: 0,
      behavior: 'smooth',
    });
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text);
  }

  handleError(error: any, caught?: Observable<any>) {
    return observableOf(null);
  }
}

export class FilterGroup {
  operator: 'all' | 'any' = 'all';
  filters: (Filter | FilterGroup)[] = [];

  constructor(public id: number) {}

  get xpath(): string {
    const join = this.operator === 'any' ? ' or ' : ' and ';
    return this.filters
      .map(f => {
        const x = f.xpath;
        if (x == null) return '';
        return '(' + x + ')';
      })
      .filter(f => f !== '')
      .join(join);
  }
}

export class Filter {
  constructor(
    public id: number,
    public attribute: DisplayAttribute,
  ) {
    if (this.attribute.Multivalued) {
      this.keyword = 'contains';
    } else {
      switch (this.attribute.DataType) {
        case 'Boolean':
        case 'Integer':
        case 'Reference':
        case 'ObjectType':
          this.keyword = '=';
          break;
        case 'DateTime':
          this.keyword = '>';
          this.search = 1;
          this.dateTimeCase = 'daysAgo';
          break;
        case 'String':
        case 'Text':
          this.keyword = 'starts-with';
      }
    }
  }

  public keyword: string; //starts-with, contains, ...
  public negate: boolean = false;
  public search: any;
  public dateTimeCase?: 'specificDate' | 'today' | 'daysAgo' | 'daysFromToday' = null;

  get xpath(): string {
    if (this.keyword == null) return null;
    let s;

    if (
      this.attribute.DataType === 'Integer' ||
      (this.attribute.DataType === 'DateTime' &&
        ['daysAgo', 'daysFromToday'].indexOf(this.dateTimeCase) !== -1)
    ) {
      if (this.search == null) return null;
      if (typeof this.search !== 'number') {
        s = parseInt(this.search);
      } else {
        s = Math.round(this.search);
      }
    } else if (this.attribute.DataType === 'DateTime') {
      if (this.dateTimeCase === 'specificDate') {
        if (this.search == null) return null;
        const date: Date = new Date(this.search);
        if (date == null) return null;
        s =
          '"' +
          CtrlDatetimeComponent.getNumberAsString(date.getUTCFullYear(), 4) +
          '-' +
          CtrlDatetimeComponent.getNumberAsString(date.getUTCMonth() + 1, 2) +
          '-' +
          CtrlDatetimeComponent.getNumberAsString(date.getUTCDate(), 2) +
          'T' +
          CtrlDatetimeComponent.getNumberAsString(date.getUTCHours(), 2) +
          ':' +
          CtrlDatetimeComponent.getNumberAsString(date.getUTCMinutes(), 2) +
          ':' +
          CtrlDatetimeComponent.getNumberAsString(date.getUTCSeconds(), 2) +
          '.' +
          CtrlDatetimeComponent.getNumberAsString(date.getUTCMilliseconds(), 3) +
          '"';
      } else {
        s = '';
      }
    } else if (this.keyword !== 'LoggedInUser' && this.keyword !== 'TargetObject') {
      if (this.search == null) return null;
      if (typeof this.search === 'string') {
        s = this.search.replace(/'/g, '&apos;').replace(/"/g, '&quot;');
        s = '"' + s + '"';
      }
    }

    let q;

    if (this.attribute.DataType === 'DateTime') {
      switch (this.dateTimeCase) {
        case 'specificDate':
          q = this.attribute.Name + this.keyword + s;
          break;
        case 'today':
          q = this.attribute.Name + this.keyword + 'fn:current-dateTime()';
          break;
        case 'daysAgo':
          if (!(typeof s === 'number') || isNaN(s)) return null;
          if (s < 0) s = 0;
          q =
            this.attribute.Name +
            this.keyword +
            "op:subtract-dayTimeDuration-from-dateTime(fn:current-dateTime(), xs:dayTimeDuration('P" +
            s +
            "D'))";
          break;
        case 'daysFromToday':
          if (!(typeof s === 'number') || isNaN(s)) return null;
          if (s < 0) s = 0;
          q =
            this.attribute.Name +
            this.keyword +
            "op:add-dayTimeDuration-to-dateTime(fn:current-dateTime(), xs:dayTimeDuration('P" +
            s +
            "D'))";
          break;
        default:
          return null;
      }
    } else if (this.keyword === 'in') {
      q =
        this.attribute.Name +
        ' = /*[' +
        FilterbuilderComponent.getAttributeName('ObjectID') +
        ' = ' +
        s +
        ']/' +
        FilterbuilderComponent.getAttributeName('ComputedMember');
    } else if (this.keyword.charCodeAt(0) < 65) {
      // =, <, > ...
      q = this.attribute.Name + this.keyword + s;
    } else if (this.keyword === 'LoggedInUser') {
      q = this.attribute.Name + '="%LoginID%"';
    } else if (this.keyword === 'TargetObject') {
      q = this.attribute.Name + '="%ObjectID%"';
    } else {
      // text keyword
      q = this.keyword + '(' + this.attribute.Name + ',' + s + ')';
    }

    if (this.negate) {
      return 'not(' + q + ')';
    } else {
      return q;
    }
  }
}
